// Allowance arithmetic — the pure half of the node/network standing line (app#86).
//
// Every decision that can be got wrong numerically lives here, with no React and no store, so it
// can be exercised with `node --experimental-strip-types src/utilities/allowance.test.ts` (this
// repo has no test runner and is not getting one).
//
// The input is ONE SAMPLE, not a series. `get_org_usage` returns a single period-to-date row per
// axis — used / included / overage / overage_cents — so nothing here may pretend to know a shape
// over time. The projection below is a deliberate straight line from a single point, and it is
// labelled as an estimate everywhere it is rendered.

// `included == null` means UNLIMITED, and it arrives that way from `get_org_usage`: a non-metered
// (Enterprise) plan, and — since router migration 0106 — any axis whose plan column is `<= 0`.
// ⚠ It is NOT the same as `included === 0`. Bandwidth deliberately keeps `0` meaning "an allowance
// of zero" (the router's own note: nothing reads bandwidth_bytes_free <= 0 as unlimited), so a 0
// here must band as `over` the moment a single byte moves, never as unlimited.
export type AllowanceBand = 'unlimited' | 'ok' | 'near' | 'over'

// The near-the-limit edge. 0.8 is not a taste decision: it is the fraction at which the router
// already writes an `allowance_80` billing alert and emails the customer (src/billing/alerts.go),
// so this bar turns amber at the same moment the customer is told. Usage.tsx's `UsageBar` bands at
// 0.8/0.95 instead; that third edge is cosmetic there and is deliberately not copied, because a
// colour change nobody was warned about is what makes a warning read as noise.
export const NEAR_FRACTION = 0.8

// used / included, or null when there is nothing to divide by (unlimited). Clamped at nothing —
// the caller wants to know it is at 240%, and only the BAR width is clamped, at the render.
export function allowanceFraction(used: number, included: number | null): number | null {
  if (included == null || !Number.isFinite(included) || included <= 0) return null
  if (!Number.isFinite(used) || used <= 0) return 0
  return used / included
}

export function allowanceBand(used: number, included: number | null): AllowanceBand {
  // ⚠ An `included` of 0 is an allowance of zero, not unlimited — it bands on the USED value.
  if (included === 0) return used > 0 ? 'over' : 'ok'
  const fraction = allowanceFraction(used, included)
  if (fraction == null) return 'unlimited'
  if (fraction >= 1) return 'over'
  if (fraction >= NEAR_FRACTION) return 'near'
  return 'ok'
}

// MUI severity/colour for a band. `unlimited` reads as `ok`: there is no limit to be near.
export function bandSeverity(band: AllowanceBand): 'success' | 'warning' | 'error' {
  if (band === 'over') return 'error'
  if (band === 'near') return 'warning'
  return 'success'
}

// This node's / this network's share of the org-wide bandwidth total, 0..1. The allowance pool is
// an ORG pool while the matrix beside it is one node or one network, so the share is the only
// honest bridge between the two numbers.
// ⚠ Returns null rather than 0 when the whole is 0 or the part is unknown: "0% of the allowance"
// and "we cannot say" look identical on screen and mean opposite things.
export function shareFraction(part: number | null, whole: number): number | null {
  if (part == null || !Number.isFinite(part) || part < 0) return null
  if (!Number.isFinite(whole) || whole <= 0) return null
  return Math.min(1, part / whole)
}

// How much of the period has elapsed, 0..1. Mirrors `projectedBillCents` in models/useUsage.ts
// exactly — a PAST period is fully elapsed and returns 1, so its projection is its actual.
export function elapsedFraction(startMs: number, endMs: number, now: number): number {
  const total = endMs - startMs
  const elapsed = now - startMs
  if (total <= 0 || elapsed >= total) return 1
  if (elapsed <= 0) return 1
  return elapsed / total
}

// Period-to-date usage extrapolated linearly to the end of the period. Returns null for a period
// that has fully elapsed — there is nothing left to project, and drawing "on track for" over a
// finished month is just the actual wearing a guess's clothes.
export function projectedUsage(used: number, startMs: number, endMs: number, now: number): number | null {
  const fraction = elapsedFraction(startMs, endMs, now)
  if (fraction >= 1) return null
  if (!Number.isFinite(used) || used <= 0) return 0
  return used / fraction
}

// When, at the current average rate, the allowance runs out — epoch ms, or null when it does not
// run out inside this period (or cannot be computed).
//
// ⚠⚠ THE DATE IS THE POINT OF THIS WHOLE FILE. The throttle and the block are live while billing
// is dark, so a user can be shaped or stopped with nothing on screen that saw it coming. A
// percentage says where they are; only a date says when it bites.
//
// ⚠ Already over ⇒ null, not "now". "Runs out on the 4th" is a forecast, and a forecast of a
// thing that has happened is the wrong sentence — the caller has `band === 'over'` for that, and
// it says so in the past tense.
export function exhaustionAt(
  used: number,
  included: number | null,
  startMs: number,
  endMs: number,
  now: number
): number | null {
  if (included == null || !Number.isFinite(included) || included <= 0) return null
  if (!Number.isFinite(used) || used <= 0) return null
  if (used >= included) return null
  const elapsed = now - startMs
  if (elapsed <= 0 || now >= endMs) return null
  const perMs = used / elapsed
  if (perMs <= 0) return null
  const at = now + (included - used) / perMs
  // Outside the period the allowance resets first, so nothing runs out. `>=` rather than `>`:
  // landing exactly on the reset instant is the allowance surviving the period.
  return at >= endMs ? null : at
}
